import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import dayjs from "dayjs";

const useStyles = makeStyles({
  card: {
    marginBottom: 12,
    border: "1px solid #e0e0e0",
    boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.14)",
  },
  cardContent: {
    paddingBottom: 8,
  },
  header: {
    marginBottom: 12,
  },
  subject: {
    fontWeight: 600,
    fontSize: 16,
    color: "#006EB3",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  infoGrid: {
    marginTop: 8,
  },
  label: {
    fontSize: 12,
    color: "#666",
    marginBottom: 4,
  },
  value: {
    fontWeight: 500,
    fontSize: 14,
  },
  cardActions: {
    padding: "8px 16px",
    paddingTop: 0,
  },
});

const getStatusColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case "delivered":
      return "#4caf50";
    case "sent":
      return "#2196f3";
    case "failed":
      return "#f44336";
    case "bounced":
      return "#ff9800";
    case "opened":
      return "#9c27b0";
    default:
      return "#757575";
  }
};

interface EmailLog {
  coach?: React.ReactNode;
  recipientCount?: React.ReactNode;
  sentAt?: string;
  status: string;
  subject: string;
}

interface EmailLogCardProps {
  email: EmailLog;
  onViewDetails?: () => void;
}

export default function EmailLogCard({ email, onViewDetails }: EmailLogCardProps): React.ReactElement {
  const classes = useStyles();

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <Typography className={classes.subject} title={email.subject}>
            {email.subject || "No Subject"}
          </Typography>
        </div>

        <Grid container spacing={2} className={classes.infoGrid}>
          <Grid item xs={12}>
            <Typography className={classes.label}>Date & Time</Typography>
            <Typography className={classes.value}>
              {dayjs(email.sentAt).format("DD/MM/YYYY HH:mm")}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography className={classes.label}>Recipients</Typography>
            <Typography className={classes.value}>
              {email.recipientCount || 0}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography className={classes.label}>Coach</Typography>
            <Typography className={classes.value}>
              {email.coach || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.label}>Status</Typography>
            <Chip
              label={email.status || "Unknown"}
              size="small"
              style={{
                backgroundColor: getStatusColor(email.status),
                color: "#fff",
                fontWeight: 500,
              }}
            />
          </Grid>
        </Grid>
      </CardContent>

      <CardActions className={classes.cardActions}>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={onViewDetails}
        >
          View Details
        </Button>
      </CardActions>
    </Card>
  );
}
